import { pl } from './pl/index.js';

/**
 * The game in more than one language, keyed on the English.
 *
 * Every string in the UI is written in English at the place it is used, wrapped in `t()`,
 * and the English is the key: `t('Race here')` looks `'Race here'` up in the Polish
 * dictionary and falls back to itself. The alternative — `t('wizard.confirm')` with the
 * English in a dictionary of its own — makes every call site unreadable for the sake of a
 * key nobody needs: a reader of `raceWizard.ts` wants to see what the button says. See
 * DECISIONS.md §65.
 *
 * Placeholders are `{name}`, filled from `params`. Plurals go through `plural()`, because
 * Polish has three of them where English has two, and no amount of `n === 1 ? … : …` at
 * the call site gets "5 wyścigów" right.
 *
 * The language is decided once, when this module is first imported, and changing it reloads
 * the page. Everything in the UI is built once in a constructor, so translating it in place
 * would mean every panel learning to rebuild itself — for a choice somebody makes once.
 */

export type Lang = 'en' | 'pl';

/** In the order the picker shows them, each named in its own language. */
export const LANGUAGES: readonly { code: Lang; name: string }[] = [
  { code: 'en', name: 'English' },
  { code: 'pl', name: 'Polski' },
];

/** A Polish plural: `one` for 1, `few` for 2–4, 22–24…, `many` for the rest, `other` for fractions. */
export interface PluralForms {
  one: string;
  few: string;
  many: string;
  other?: string;
}

export type Dictionary = Record<string, string | PluralForms>;

export type Params = Record<string, string | number>;

const DICTIONARIES: Record<Lang, Dictionary> = { en: {}, pl };

/** The language a reader explicitly picked. Absent means "follow the browser". */
export const LANG_KEY = 'osm-racer.lang';

/**
 * The stored choice if there is one, else the first browser language we speak, else English.
 *
 * The browser's whole list rather than just its first entry, because a Pole whose system is
 * in English often lists `pl` second — and would still rather read Polish than English.
 */
export function detectLanguage(stored: string | null | undefined, preferred: readonly string[]): Lang {
  if (isLang(stored)) return stored;
  for (const tag of preferred) {
    const primary = tag.toLowerCase().split('-')[0];
    if (isLang(primary)) return primary;
  }
  return 'en';
}

function isLang(value: unknown): value is Lang {
  return value === 'en' || value === 'pl';
}

/**
 * Only in a browser. Node has a `navigator` too, and its language is the machine's — so a
 * test run on a Polish laptop would otherwise see Polish where every assertion expects the
 * English it was written against.
 */
function initialLanguage(): Lang {
  if (typeof document === 'undefined') return 'en';
  let stored: string | null = null;
  try {
    stored = localStorage.getItem(LANG_KEY);
  } catch {
    // Storage blocked: follow the browser.
  }
  return detectLanguage(stored, navigator.languages?.length ? navigator.languages : [navigator.language]);
}

let current: Lang = initialLanguage();
let rules = new Intl.PluralRules(current);
// For the screen reader's voice and the browser's hyphenation, and so the translate prompt
// does not offer to translate a page that is already in the reader's language.
if (typeof document !== 'undefined') document.documentElement.lang = current;

export function language(): Lang {
  return current;
}

/** BCP 47 tag for `Intl` and `toLocaleString`: Polish, or British English to match the copy. */
export function locale(): string {
  return current === 'pl' ? 'pl-PL' : 'en-GB';
}

/** For tests, and for the page's first paint. The UI itself changes language by reloading. */
export function setLanguage(lang: Lang): void {
  current = lang;
  rules = new Intl.PluralRules(lang);
  if (typeof document !== 'undefined') document.documentElement.lang = lang;
}

/** Stores the choice and reloads, so every panel is rebuilt in it. Null goes back to the browser's. */
export function chooseLanguage(lang: Lang | null): void {
  try {
    if (lang) localStorage.setItem(LANG_KEY, lang);
    else localStorage.removeItem(LANG_KEY);
  } catch {
    // Not remembered, but still applied to this page load.
  }
  location.reload();
}

function fill(template: string, params?: Params): string {
  if (!params) return template;
  return template.replace(/\{(\w+)\}/g, (whole, name: string) =>
    name in params ? String(params[name]) : whole,
  );
}

/** The translation of `english`, with `{placeholders}` filled in. */
export function t(english: string, params?: Params): string {
  const entry = DICTIONARIES[current][english];
  const template = typeof entry === 'string' ? entry : entry ? (entry.other ?? entry.many) : english;
  return fill(template, params);
}

/**
 * A counted phrase. `other` is the English plural and the dictionary key; `{n}` is the count.
 *
 *     plural(laps, '{n} lap', '{n} laps')   // "3 laps", "3 okrążenia", "5 okrążeń"
 */
export function plural(n: number, one: string, other: string, params?: Params): string {
  const entry = DICTIONARIES[current][other];
  const all = { n, ...params };
  if (!entry) return fill(n === 1 ? one : other, all);
  if (typeof entry === 'string') return fill(entry, all);
  const form = rules.select(n) as keyof PluralForms;
  return fill(entry[form] ?? entry.other ?? entry.many, all);
}

/** A number the way the reader writes one: "1,240" and "2.1" in English, "1240" and "2,1" in Polish. */
export function num(value: number, digits?: number): string {
  return value.toLocaleString(locale(), digits === undefined ? {} : { minimumFractionDigits: digits, maximumFractionDigits: digits });
}

// ---------------------------------------------------------------------------
// The server's words
// ---------------------------------------------------------------------------

/**
 * Compiled once per language: every dictionary key with a placeholder, as a regex that
 * matches the English it formats to.
 */
let patterns: { lang: Lang; list: { re: RegExp; names: string[]; key: string }[] } | null = null;

function serverPatterns() {
  if (patterns?.lang === current) return patterns.list;
  const list: { re: RegExp; names: string[]; key: string }[] = [];
  for (const key of Object.keys(DICTIONARIES[current])) {
    if (!key.includes('{')) continue;
    const names: string[] = [];
    const source = key
      .split(/(\{\w+\})/)
      .map((part) => {
        const m = /^\{(\w+)\}$/.exec(part);
        if (!m) return part.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        names.push(m[1]);
        return '(.+?)';
      })
      .join('');
    list.push({ re: new RegExp(`^${source}$`, 's'), names, key });
  }
  // Longest first, so "Pick between 0 and {n} checkpoints." is tried before anything shorter
  // that would also swallow it.
  list.sort((a, b) => b.key.length - a.key.length);
  patterns = { lang: current, list };
  return list;
}

/**
 * Translates a message that arrived from the server already formatted in English.
 *
 * The server stays English — one lobby has players in both languages, and the message is
 * broadcast once — and its sentences are keys in the dictionary like any other. A sentence
 * with a value in it is matched against the keys with placeholders and the values carried
 * across, each translated again in case it is itself a server sentence:
 * `"Kraków Cup" cannot be raced: {reason}` nests the reason. Anything unrecognised comes
 * through in English, which is better than dropping it.
 */
export function serverText(message: string): string {
  if (current === 'en' || !message) return message;
  const exact = DICTIONARIES[current][message];
  if (exact) return t(message);
  for (const { re, names, key } of serverPatterns()) {
    const m = re.exec(message);
    if (!m) continue;
    const params: Params = {};
    names.forEach((name, i) => (params[name] = serverText(m[i + 1])));
    return t(key, params);
  }
  return message;
}
